import { useCallback, useEffect, useRef, useState } from "react";
import type { TextareaHTMLAttributes } from "react";
import { Textarea } from "@/design-system/components/ui";
import { UserSuggestionList } from "@/components/users/UserSuggestionList";
import { useUserSearch } from "../hooks/useWorkTasks";

interface Props
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "value" | "onChange"> {
  value: string;
  onChange: (next: string) => void;
}

/**
 * Tasks Module v1.0 § 8 / PR 3 — comment editor with @mention autocomplete.
 * Tasks v1.1 — refactored to share <c>UserSuggestionList</c> with the
 * standalone <c>UserPicker</c> component used by the assignee editor.
 *
 * Detects an active "@" trigger by scanning backwards from the caret to
 * the closest space or newline. While the trigger is active, the
 * dropdown lists matching active users; pressing Enter / Tab or
 * clicking a suggestion inserts the chosen display name and replaces
 * the partial query. Escape and onBlur both dismiss the dropdown.
 *
 * The suppress-next-detect flag (<c>justSelectedRef</c>) prevents the
 * post-insert focus restore from reopening the dropdown against the
 * freshly-inserted "@DisplayName " run — this fix shipped in PR #92.
 */
export function MentionTextarea({
  value,
  onChange,
  onKeyDown,
  onBlur,
  ...textareaProps
}: Props) {
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const [trigger, setTrigger] = useState<{ start: number; query: string } | null>(null);
  const [highlighted, setHighlighted] = useState(0);
  const justSelectedRef = useRef(false);
  const idPrefix = useRef(`mt-${Math.random().toString(36).slice(2, 8)}`).current;

  const search = useUserSearch(trigger?.query ?? null, 8);
  const matches = trigger ? search.data ?? [] : [];

  useEffect(() => {
    setHighlighted(0);
  }, [trigger?.query]);

  const closeDropdown = useCallback(() => {
    setTrigger(null);
    setHighlighted(0);
  }, []);

  function detectTrigger(text: string, caret: number) {
    if (justSelectedRef.current) return null;
    const before = text.slice(0, caret);
    const at = before.lastIndexOf("@");
    if (at < 0) return null;
    const prev = at === 0 ? "" : before[at - 1];
    if (prev && !/\s/.test(prev)) return null;
    const fragment = before.slice(at + 1);
    if (fragment.includes("\n")) return null;
    if (fragment.length > 30) return null;
    return { start: at, query: fragment };
  }

  const selectSuggestion = useCallback(
    (displayName: string) => {
      if (!trigger) return;
      const before = value.slice(0, trigger.start);
      const after = value.slice(trigger.start + 1 + trigger.query.length);
      const insert = `@${displayName} `;
      const next = before + insert + after;
      justSelectedRef.current = true;
      onChange(next);
      closeDropdown();
      const caret = before.length + insert.length;
      requestAnimationFrame(() => {
        const el = ref.current;
        if (!el) return;
        el.focus();
        el.setSelectionRange(caret, caret);
      });
    },
    [trigger, value, onChange, closeDropdown],
  );

  return (
    <div className="relative">
      <Textarea
        ref={ref}
        value={value}
        onChange={(e) => {
          justSelectedRef.current = false;
          onChange(e.target.value);
          const caret = e.target.selectionStart ?? e.target.value.length;
          setTrigger(detectTrigger(e.target.value, caret));
        }}
        onSelect={(e) => {
          const el = e.currentTarget;
          setTrigger(detectTrigger(el.value, el.selectionStart ?? 0));
        }}
        onBlur={(e) => {
          closeDropdown();
          onBlur?.(e);
        }}
        onKeyDown={(e) => {
          if (trigger && matches.length > 0) {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setHighlighted((i) => (i + 1) % matches.length);
              return;
            }
            if (e.key === "ArrowUp") {
              e.preventDefault();
              setHighlighted((i) => (i - 1 + matches.length) % matches.length);
              return;
            }
            if (e.key === "Enter" || e.key === "Tab") {
              e.preventDefault();
              selectSuggestion(matches[highlighted].displayName);
              return;
            }
            if (e.key === "Escape") {
              e.preventDefault();
              closeDropdown();
              return;
            }
          }
          onKeyDown?.(e);
        }}
        {...textareaProps}
      />
      {trigger && matches.length > 0 && (
        <UserSuggestionList
          idPrefix={idPrefix}
          matches={matches}
          highlighted={highlighted}
          ariaLabel="Mention suggestions"
          onSelect={(_id, displayName) => selectSuggestion(displayName)}
        />
      )}
    </div>
  );
}
